import omit from 'lodash/omit'
import { Maybe } from '../../../utils/classes/graphql'
import { getValue } from '../../../utils/valueAndKeyHelpers'
import {
  DenseTableRowProps,
  CellProps,
  DenseTableEditableAndStyleRowProps
} from '../types'

interface KeysAndColumnNameWrapperProps<RowDataType>
  extends CellProps<RowDataType> {
  translationIdPostfix: string
}

export const createKeysAndColumnNameArray = <RowDataType>(
  translationIdPrefix: string,
  keysAndColunmNamesArray: KeysAndColumnNameWrapperProps<RowDataType>[]
): CellProps<RowDataType>[] => {
  return keysAndColunmNamesArray.map((keysAndColunmNames) => {
    const { translationIdPostfix, ...rest } = keysAndColunmNames
    return {
      ...rest,
      labelTranslationId: translationIdPrefix + translationIdPostfix
    }
  })
}

export const omitExtraRowColumns = <RowDataType>(
  row: DenseTableRowProps<RowDataType>,
  extraOmittableColumns: (keyof RowDataType)[] = []
) => {
  const editableColumns: (keyof DenseTableEditableAndStyleRowProps<RowDataType>)[] =
    ['isRowEditable', 'isRowNew', 'rowOptions', 'addAlertClass']
  const omittableColumns = [...editableColumns, ...extraOmittableColumns]
  return omit(row, omittableColumns)
}

export const getDenseTableRowProps = <RowDataType>(
  data?: Maybe<RowDataType>[] | null
) => {
  return (getValue(data) || []) as DenseTableRowProps<RowDataType>[]
}

export const getEditableAndNewTableRows = <RowDataType>(
  rows?: DenseTableRowProps<RowDataType>[]
) =>
  rows?.filter((row) => {
    return row.isRowEditable || row.isRowNew
  }) || []

export const getEditableTableRows = <RowDataType>(
  rows?: DenseTableRowProps<RowDataType>[]
) =>
  rows?.filter((row) => {
    return row.isRowEditable
  }) || []

export const getNewTableRows = <RowDataType>(
  rows?: DenseTableRowProps<RowDataType>[]
) => {
  return rows
    ? rows.filter((row) => {
        return row.isRowNew
      })
    : []
}

export const getOldTableRows = <RowDataType>(
  rows?: DenseTableRowProps<RowDataType>[]
) =>
  rows?.filter((row) => {
    return row.isRowNew !== true
  }) || []

export const isEditableTableCell = <RowDataType>(
  row: DenseTableRowProps<RowDataType>,
  inputTypeProps: CellProps<RowDataType>['inputTypeProps']
) => {
  const isEditableNewItem =
    row.isRowNew === true &&
    inputTypeProps?.isEditingDisabledForNewItem !== true
  const isEditableOldItem =
    row.isRowEditable === true &&
    inputTypeProps?.isEditingDisabledForOldItem !== true &&
    inputTypeProps?.showOnlyForNewItem !== true

  return isEditableNewItem || isEditableOldItem
}

export const hasEditableTableCells = <RowDataType>(
  rows: DenseTableRowProps<RowDataType>[],
  recipes: CellProps<RowDataType>[]
) => {
  const newRows = getNewTableRows(rows)

  const editableRows = getEditableTableRows(rows)

  const hasOldEditableCells = recipes.find((recipe) => {
    const inputTypeProps = recipe.inputTypeProps
    return (
      !inputTypeProps?.showOnlyForNewItem &&
      !inputTypeProps?.isEditingDisabledForOldItem
    )
  })

  const hasNewEditableCells = recipes.find((recipe) => {
    const inputTypeProps = recipe.inputTypeProps
    return (
      inputTypeProps?.showOnlyForNewItem &&
      !inputTypeProps?.isEditingDisabledForNewItem
    )
  })

  return (
    (hasNewEditableCells !== undefined && newRows.length > 0) ||
    (hasOldEditableCells !== undefined && editableRows.length > 0)
  )
}
